import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import GroupIcon from "@mui/icons-material/Group";
import {
	Box,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid2,
	IconButton,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useMemo } from "react";

import { LaborStandard, LaborStandardOperation, LaborStandardPhase } from "../../../services/labor-standards-api";
import { planner, roleColor, roleTextColor } from "../planner-ui";
import { formatCrew, isMachinePhase, machinePhaseLabel, standardWorkstations } from "../utils/standard-matching";
import { formatDuration, machineBarSx, machineLineLabel } from "./utils/standard-helpers";

interface LaborStandardPreviewDialogProps {
	open: boolean;
	onClose: () => void;
	standard: LaborStandard;
}

// ---- Shared design-system styling for this dialog ----

// Panels are hairline-bordered surfaces; no resting shadow (design rule 1).
const panelSx = {
	p: 2,
	bgcolor: planner.surface,
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	borderRadius: `${planner.radius}px`,
	boxShadow: planner.shadowRest,
} as const;

// Neutral count/label chip.
const chipSx = {
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: planner.surfaceSubtle,
	border: `1px solid ${planner.hairlineStrong}`,
	color: planner.inkSecondary,
	fontFamily: planner.fontUi,
	fontSize: 11,
	fontWeight: 600,
	"& .MuiChip-icon": { color: planner.inkSecondary },
} as const;

// Key figure chip — the one brick-tinted accent per panel.
const chipAccentSx = {
	...chipSx,
	bgcolor: planner.accentSoft,
	border: `1px solid ${planner.accentBorder}`,
	color: planner.accentDeep,
	fontFamily: planner.fontMono,
	"& .MuiChip-icon": { color: planner.accentDeep },
} as const;

// Category tag: the operation's OperationType, quiet context beside the
// operation's own name. Same alloy-on-hairline ground as the neutral chip.
const categoryTagSx = {
	...planner.labelCaps,
	fontFamily: planner.fontUi,
	fontSize: 10,
	display: "inline-flex",
	alignItems: "center",
	px: 0.6,
	py: 0.2,
	borderRadius: `${planner.radiusXs}px`,
	border: `1px solid ${planner.hairline}`,
	bgcolor: planner.surfaceSubtle,
	color: planner.inkMuted,
	whiteSpace: "nowrap",
	flexShrink: 0,
} as const;

// An operation leads with its own name; the type is only the category.
const operationLabel = (op: LaborStandardOperation, fallback: string) =>
	op.name || op.operationTypeName || op.operationType?.name || fallback;

// Shown only when it differs from the name — otherwise it just repeats it.
const operationCategory = (op: LaborStandardOperation) => {
	const category = op.operationTypeName || op.operationType?.name || "";
	return category && category !== operationLabel(op, "") ? category : "";
};

const scaleLabelSx = {
	...planner.type.meta,
	fontFamily: planner.fontMono,
	color: planner.inkMuted,
	fontVariantNumeric: "tabular-nums",
} as const;

const tableHeadCellSx = {
	...planner.labelCaps,
	fontFamily: planner.fontUi,
	color: planner.inkSecondary,
	borderBottomColor: planner.hairline,
} as const;

const tableCellSx = {
	...planner.type.body,
	fontFamily: planner.fontUi,
	color: planner.ink,
	borderBottomColor: planner.hairline,
} as const;

const monoTableCellSx = {
	...tableCellSx,
	fontFamily: planner.fontMono,
	fontVariantNumeric: "tabular-nums",
} as const;

// Timeline bar sizing: bars are percent-positioned against the full scale, so
// short phases (a 6-min inspection on a 16h scale) need a floor to stay visible,
// and labels only render when the bar is wide enough to hold them.
const MIN_BAR_PCT = 2.5;
const LABEL_MIN_PCT = 9;

const getRenderedRange = (startMin: number, endMin: number, scaleMin: number) => {
	const startPct = scaleMin > 0 ? (startMin / scaleMin) * 100 : 0;
	const rawWidthPct = scaleMin > 0 ? ((endMin - startMin) / scaleMin) * 100 : 0;
	const widthPct = Math.max(rawWidthPct, MIN_BAR_PCT);
	const leftPct = Math.min(startPct, Math.max(0, 100 - widthPct));
	return { leftPct, widthPct, endPct: leftPct + widthPct };
};

// Distinct roles in the order they first appear on the timeline.
const rolesInOrder = (roles: string[]) => Array.from(new Set(roles));

// A bar narrower than LABEL_MIN_PCT renders as a colour-only box, so colour is
// the only thing carrying meaning. The legend is therefore part of the timeline
// itself — read before the bars, not hunted for below the fold — and the hover
// tooltips stay as the detail layer.
const RoleLegend: React.FC<{ roles: string[]; sx?: object }> = ({ roles, sx }) => {
	if (roles.length === 0) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", ...sx }}>
			{roles.map((role) => (
				<Box key={role} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: "50%",
							bgcolor: roleColor(role),
							flexShrink: 0,
						}}
					/>
					<Typography
						sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkSecondary }}
					>
						{role}
					</Typography>
				</Box>
			))}
		</Box>
	);
};

// Overall Timeline Component - shows all phases grouped by operation with headers
const OverallTimeline: React.FC<{
	standard: LaborStandard;
}> = ({ standard }) => {
	// Group phases by operation
	const operationGroups: {
		name: string;
		category: string;
		phases: {
			start: number;
			end: number;
			workers: number;
			jobRole: string;
			/** Machine time: drawn as occupancy, never counted as a crew. */
			machine: boolean;
			machineLabel: string;
		}[];
	}[] = [];

	// Operations run sequentially unless they share a parallelGroup, in which case
	// they start together and the group lasts as long as its longest member.
	// Grouping is by VALUE, not by row position: members need not be adjacent, and
	// grouping a later operation with an earlier one pulls it into that slot.
	// phase.startOffsetMinutes is relative to its own operation, so each operation
	// is offset by everything that ran before its group.
	const groupKeyOf = (op: LaborStandardOperation) =>
		op.parallelGroup != null ? `g${op.parallelGroup}` : `seq${op.sequence}`;

	const groupOrder: string[] = [];
	const membersByGroup = new Map<string, LaborStandardOperation[]>();
	(standard.operations || []).forEach((op) => {
		const key = groupKeyOf(op);
		if (!membersByGroup.has(key)) {
			membersByGroup.set(key, []);
			groupOrder.push(key);
		}
		membersByGroup.get(key)?.push(op);
	});

	let cumulativeStart = 0;
	groupOrder.forEach((key) => {
		const members = membersByGroup.get(key) || [];
		let groupSpan = 0;

		members.forEach((op) => {
			const opPhases: (typeof operationGroups)[number]["phases"] = [];
			let opEnd = op.durationMinutes || 0;
			op.phases?.forEach((phase) => {
				opPhases.push({
					start: cumulativeStart + phase.startOffsetMinutes,
					end: cumulativeStart + phase.startOffsetMinutes + phase.durationMinutes,
					workers: phase.workersRequired,
					jobRole: phase.jobRole,
					machine: isMachinePhase(phase),
					machineLabel: machinePhaseLabel(phase),
				});
				opEnd = Math.max(opEnd, phase.startOffsetMinutes + phase.durationMinutes);
			});
			if (opPhases.length > 0) {
				operationGroups.push({
					name: operationLabel(op, "Operation"),
					category: operationCategory(op),
					phases: opPhases,
				});
			}
			groupSpan = Math.max(groupSpan, opEnd);
		});

		// The group releases the unit only once its longest member is done.
		cumulativeStart += groupSpan;
	});

	const totalDuration = Math.max(cumulativeStart, standard.totalDurationMinutes || 0, 1);
	const allPhases = operationGroups.flatMap((g) => g.phases);
	// Counts and the colour key read the LABOR phases; machine lines are still
	// drawn as occupancy.
	const crewPhases = allPhases.filter((p) => !p.machine);
	if (allPhases.length === 0) return null;

	// Group phases by vertical row using rendered percent ranges, so
	// minimum-width bars never visually collide.
	const getPhaseRows = (phases: typeof allPhases) => {
		const rows: typeof phases[] = [];
		phases.forEach((phase) => {
			const { leftPct, endPct } = getRenderedRange(phase.start, phase.end, totalDuration);
			let placed = false;
			for (const row of rows) {
				const hasOverlap = row.some((p) => {
					const other = getRenderedRange(p.start, p.end, totalDuration);
					return !(endPct <= other.leftPct || leftPct >= other.endPct);
				});
				if (!hasOverlap) {
					row.push(phase);
					placed = true;
					break;
				}
			}
			if (!placed) {
				rows.push([phase]);
			}
		});
		return rows;
	};

	return (
		<Paper elevation={0} sx={{ ...panelSx, mb: 3 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
				<Typography sx={{ ...planner.type.title, fontFamily: planner.fontUi, color: planner.ink }}>
					Complete job order sequence
				</Typography>
				<Chip
					icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
					label={formatDuration(totalDuration)}
					size="small"
					sx={chipAccentSx}
				/>
				<Chip label={`${crewPhases.length} phases`} size="small" sx={chipSx} />
			</Box>

			{/* Legend first: the short bars below carry no label, so the colour key
			    has to be readable before the timeline is scanned. */}
			<RoleLegend roles={rolesInOrder(crewPhases.map((p) => p.jobRole))} sx={{ mb: 1.5 }} />

			{/* Timeline visualization */}
			<Box sx={{ mb: 2 }}>
				{/* Time scale */}
				<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, px: 0.5 }}>
					{[0, 0.25, 0.5, 0.75, 1].map((fraction) => (
						<Typography key={fraction} sx={scaleLabelSx}>
							{formatDuration(Math.round(totalDuration * fraction))}
						</Typography>
					))}
				</Box>

				{/* Background grid */}
				<Box
					sx={{
						position: "relative",
						bgcolor: planner.surfaceSubtle,
						borderRadius: `${planner.radiusXs}px`,
						height: "4px",
						mb: 1,
					}}
				>
					<LinearProgress
						variant="determinate"
						value={100}
						sx={{
							height: "4px",
							borderRadius: `${planner.radiusXs}px`,
							bgcolor: planner.surfaceSubtle,
							"& .MuiLinearProgress-bar": { bgcolor: planner.hairlineStrong },
						}}
					/>
				</Box>

				{/* Operations with their phases */}
				{operationGroups.map((opGroup, opIndex) => {
					const phaseRows = getPhaseRows(opGroup.phases);
					return (
						<Box key={opIndex} sx={{ mb: 1.5 }}>
							{/* Operation header — the operation's own name leads, its type follows */}
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, pl: 0.5, minWidth: 0 }}>
								<Typography
									noWrap
									sx={{
										...planner.type.metaStrong,
										fontFamily: planner.fontUi,
										color: planner.inkSecondary,
									}}
								>
									{opIndex + 1}. {opGroup.name}
								</Typography>
								{opGroup.category ? (
									<Box component="span" sx={categoryTagSx}>
										{opGroup.category}
									</Box>
								) : null}
							</Box>

							{/* Phase bars for this operation */}
							{phaseRows.map((row, rowIndex) => (
								<Box
									key={rowIndex}
									sx={{
										position: "relative",
										height: "28px",
										mb: 0.5,
									}}
								>
									{row.map((phase, phaseIndex) => {
										const { leftPct, widthPct } = getRenderedRange(phase.start, phase.end, totalDuration);
										const showLabel = widthPct >= LABEL_MIN_PCT;
										const color = roleColor(phase.jobRole);

										// Machine time: occupancy, drawn neutral.
										if (phase.machine) {
											const label = `${phase.machineLabel} · ${formatDuration(phase.end - phase.start)}`;
											return (
												<Tooltip
													key={`machine-${phaseIndex}`}
													title={`${opGroup.name}: ${label} — occupies the unit, no workers required - ${phase.start}min to ${phase.end}min`}
												>
													<Box
														sx={{
															position: "absolute",
															left: `${leftPct}%`,
															width: `${widthPct}%`,
															height: "24px",
															borderRadius: `${planner.radiusSm}px`,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontFamily: planner.fontMono,
															fontSize: "11px",
															fontWeight: 500,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															px: 0.5,
															cursor: "default",
															...machineBarSx,
														}}
													>
														{showLabel ? label : ""}
													</Box>
												</Tooltip>
											);
										}

										return (
											<Tooltip
												key={`${phase.jobRole}-${phaseIndex}`}
												title={`${opGroup.name}: ${phase.jobRole} (${formatCrew(phase.workers)} workers) - ${phase.start}min to ${phase.end}min`}
											>
												<Box
													sx={{
														position: "absolute",
														left: `${leftPct}%`,
														width: `${widthPct}%`,
														height: "24px",
														backgroundColor: color,
														borderRadius: `${planner.radiusSm}px`,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														color: roleTextColor(color),
														fontFamily: planner.fontMono,
														fontSize: "11px",
														fontWeight: 500,
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
														px: 0.5,
														cursor: "pointer",
														border: `1px solid ${planner.paper}`,
														"&:hover": {
															borderColor: planner.navy,
															zIndex: 1,
														},
													}}
												>
													{showLabel ? `${phase.jobRole} (${formatCrew(phase.workers)})` : ""}
												</Box>
											</Tooltip>
										);
									})}
								</Box>
							))}
						</Box>
					);
				})}
			</Box>
		</Paper>
	);
};

// Operation Timeline Component
const OperationTimeline: React.FC<{
	operationName: string;
	operationCategoryName?: string;
	operationDuration: number;
	phases: LaborStandardPhase[];
	sequence: number;
	/** The work center this step runs on, and the alternatives it may run on. */
	workstation?: string | null;
	alternatives?: string[];
}> = ({ operationName, operationCategoryName, operationDuration, phases, sequence, workstation, alternatives }) => {
	// Group phases by their vertical position
	const phaseRows: LaborStandardPhase[][] = [];

	// Pack using rendered percent ranges so minimum-width bars don't collide.
	const rangeOf = (p: LaborStandardPhase) =>
		getRenderedRange(p.startOffsetMinutes, p.startOffsetMinutes + p.durationMinutes, operationDuration);

	phases.forEach((phase) => {
		const { leftPct, endPct } = rangeOf(phase);
		let placed = false;

		for (const row of phaseRows) {
			const hasOverlap = row.some((p) => {
				const other = rangeOf(p);
				return !(endPct <= other.leftPct || leftPct >= other.endPct);
			});

			if (!hasOverlap) {
				row.push(phase);
				placed = true;
				break;
			}
		}

		if (!placed) {
			phaseRows.push([phase]);
		}
	});

	// The work-phase column only appears for standards that carry the customer's
	// codes — older standards keep the table they had.
	const hasWorkPhases = phases.some((phase) => phase.workPhaseCode || phase.workPhaseName);

	// Crew phases only: machine lines are occupancy and carry no role colour.
	const crewPhases = phases.filter((phase) => !isMachinePhase(phase));

	// This section only needs its own colour key when at least one of its bars
	// is too narrow to hold a label; when every bar is labelled it would be noise.
	const hasUnlabelledBar = crewPhases.some((phase) => rangeOf(phase).widthPct < LABEL_MIN_PCT);

	return (
		<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
				<Chip label={sequence} size="small" sx={{ ...chipAccentSx, minWidth: 28 }} />
				<Typography sx={{ ...planner.type.title, fontFamily: planner.fontUi, color: planner.ink }}>
					{operationName}
				</Typography>
				{operationCategoryName ? (
					<Box component="span" sx={categoryTagSx}>
						{operationCategoryName}
					</Box>
				) : null}
				{workstation ? (
					<Chip
						label={
							alternatives && alternatives.length > 0
								? `${workstation} or ${alternatives.join(", ")}`
								: workstation
						}
						size="small"
						sx={chipAccentSx}
					/>
				) : null}
				<Chip
					icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
					label={formatDuration(operationDuration)}
					size="small"
					sx={{ ...chipSx, fontFamily: planner.fontMono }}
				/>
			</Box>

			{/* Timeline visualization */}
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, px: 0.5 }}>
					<Typography sx={scaleLabelSx}>0 min</Typography>
					<Typography sx={scaleLabelSx}>{formatDuration(operationDuration)}</Typography>
				</Box>

				<Box
					sx={{
						position: "relative",
						bgcolor: planner.surfaceSubtle,
						borderRadius: `${planner.radiusXs}px`,
						height: "4px",
						mb: 1,
					}}
				>
					<LinearProgress
						variant="determinate"
						value={100}
						sx={{
							height: "4px",
							borderRadius: `${planner.radiusXs}px`,
							bgcolor: planner.surfaceSubtle,
							"& .MuiLinearProgress-bar": { bgcolor: planner.hairlineStrong },
						}}
					/>
				</Box>

				{phaseRows.map((row, rowIndex) => (
					<Box key={rowIndex} sx={{ position: "relative", height: "28px", mb: 0.5 }}>
						{row.map((phase, phaseIndex) => {
							const { leftPct, widthPct } = getRenderedRange(
								phase.startOffsetMinutes,
								phase.startOffsetMinutes + phase.durationMinutes,
								operationDuration
							);
							const showLabel = widthPct >= LABEL_MIN_PCT;
							const color = roleColor(phase.jobRole);

							// Machine time: occupancy, drawn neutral.
							if (isMachinePhase(phase)) {
								const label = machineLineLabel(phase, phase.durationMinutes);
								return (
									<Tooltip
										key={`machine-${phase.id ?? phaseIndex}`}
										title={`${label} — occupies the unit, no workers required, ${phase.startOffsetMinutes}min - ${phase.startOffsetMinutes + phase.durationMinutes}min`}
									>
										<Box
											sx={{
												position: "absolute",
												left: `${leftPct}%`,
												width: `${widthPct}%`,
												height: "24px",
												borderRadius: `${planner.radiusSm}px`,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontFamily: planner.fontMono,
												fontSize: "11px",
												fontWeight: 500,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												px: 0.5,
												cursor: "default",
												...machineBarSx,
											}}
										>
											{showLabel ? label : ""}
										</Box>
									</Tooltip>
								);
							}

							return (
								<Tooltip
									key={`${phase.jobRole}-${phaseIndex}`}
									title={`${phase.jobRole}: ${formatCrew(phase.workersRequired)} workers, ${phase.startOffsetMinutes}min - ${phase.startOffsetMinutes + phase.durationMinutes}min`}
								>
									<Box
										sx={{
											position: "absolute",
											left: `${leftPct}%`,
											width: `${widthPct}%`,
											height: "24px",
											backgroundColor: color,
											borderRadius: `${planner.radiusSm}px`,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: roleTextColor(color),
											fontFamily: planner.fontMono,
											fontSize: "11px",
											fontWeight: 500,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											px: 0.5,
											cursor: "pointer",
											border: `1px solid ${planner.paper}`,
											"&:hover": { borderColor: planner.navy, zIndex: 1 },
										}}
									>
										{showLabel ? `${phase.jobRole} (${formatCrew(phase.workersRequired)})` : ""}
									</Box>
								</Tooltip>
							);
						})}
					</Box>
				))}

				{/* Colour key for this operation's unlabelled bars */}
				{hasUnlabelledBar ? (
					<RoleLegend roles={rolesInOrder(crewPhases.map((p) => p.jobRole))} sx={{ mt: 1 }} />
				) : null}
			</Box>

			{/* Phase details table */}
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							{hasWorkPhases ? <TableCell sx={tableHeadCellSx}>Phase</TableCell> : null}
							<TableCell sx={tableHeadCellSx}>Job Role</TableCell>
							<TableCell align="center" sx={tableHeadCellSx}>
								<GroupIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
								Workers
							</TableCell>
							<TableCell align="right" sx={tableHeadCellSx}>
								Start
							</TableCell>
							<TableCell align="right" sx={tableHeadCellSx}>
								Duration
							</TableCell>
							<TableCell align="right" sx={tableHeadCellSx}>
								End
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{phases.map((phase, idx) => (
							<TableRow key={idx} hover>
								{/* The customer's work-type code, in their vocabulary: the code in
								    mono, the phase name as quiet context beside it. */}
								{hasWorkPhases ? (
									<TableCell sx={tableCellSx}>
										{phase.workPhaseCode || phase.workPhaseName ? (
											<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
												{phase.workPhaseCode ? (
													<Typography
														component="span"
														sx={{
															...planner.type.bodyStrong,
															fontFamily: planner.fontMono,
															color: planner.accentDeep,
														}}
													>
														{phase.workPhaseCode}
													</Typography>
												) : null}
												{phase.workPhaseName ? (
													<Typography
														component="span"
														sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}
													>
														{phase.workPhaseName}
													</Typography>
												) : null}
											</Box>
										) : (
											<Typography
												component="span"
												sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}
											>
												—
											</Typography>
										)}
									</TableCell>
								) : null}
								{isMachinePhase(phase) ? (
									<>
										<TableCell sx={tableCellSx}>
											<Chip label={machineLineLabel(phase, phase.durationMinutes)} size="small" sx={chipSx} />
										</TableCell>
										<TableCell align="center" sx={{ ...tableCellSx, color: planner.inkMuted }}>
											—
										</TableCell>
									</>
								) : (
									<>
								<TableCell sx={tableCellSx}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												bgcolor: roleColor(phase.jobRole),
											}}
										/>
										{phase.jobRole}
									</Box>
								</TableCell>
								<TableCell align="center" sx={tableCellSx}>
									<Chip
										label={formatCrew(phase.workersRequired)}
										size="small"
										sx={{
											minWidth: 32,
											borderRadius: `${planner.radiusXs}px`,
											bgcolor: roleColor(phase.jobRole),
											color: roleTextColor(roleColor(phase.jobRole)),
											fontFamily: planner.fontMono,
											fontSize: 11,
											fontWeight: 500,
										}}
									/>
								</TableCell>
									</>
								)}
								<TableCell align="right" sx={monoTableCellSx}>{phase.startOffsetMinutes} min</TableCell>
								<TableCell align="right" sx={monoTableCellSx}>{formatDuration(phase.durationMinutes)}</TableCell>
								<TableCell align="right" sx={monoTableCellSx}>{phase.startOffsetMinutes + phase.durationMinutes} min</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

const LaborStandardPreviewDialog: React.FC<LaborStandardPreviewDialogProps> = ({ open, onClose, standard }) => {
	// Every work center the recipe touches: each operation's default and its
	// alternatives, falling back to the header for a legacy standard.
	const allWorkstations = useMemo(() => standardWorkstations(standard), [standard]);

	// Calculate labor summary
	const laborSummary = useMemo(() => {
		const jobRoleSummary: Record<string, { maxConcurrent: number; totalMinutes: number }> = {};
		const allPhases: { start: number; end: number; workers: number; jobRole: string }[] = [];

		standard.operations?.forEach((op) => {
			op.phases?.forEach((phase) => {
				// Machine time needs nobody: it is not labor.
				if (isMachinePhase(phase)) return;
				allPhases.push({
					start: phase.startOffsetMinutes,
					end: phase.startOffsetMinutes + phase.durationMinutes,
					workers: phase.workersRequired,
					jobRole: phase.jobRole,
				});

				if (!jobRoleSummary[phase.jobRole]) {
					jobRoleSummary[phase.jobRole] = { maxConcurrent: 0, totalMinutes: 0 };
				}
				jobRoleSummary[phase.jobRole].totalMinutes += phase.workersRequired * phase.durationMinutes;
			});
		});

		// Calculate max concurrent workers for each job role
		Object.keys(jobRoleSummary).forEach((jobRole) => {
			const rolePhases = allPhases.filter((p) => p.jobRole === jobRole);
			const timePoints = new Set<number>();
			rolePhases.forEach((p) => {
				timePoints.add(p.start);
				timePoints.add(p.end);
			});

			let maxConcurrent = 0;
			Array.from(timePoints).forEach((time) => {
				const workersAtTime = rolePhases
					.filter((p) => p.start <= time && p.end > time)
					.reduce((sum, p) => sum + p.workers, 0);
				maxConcurrent = Math.max(maxConcurrent, workersAtTime);
			});
			jobRoleSummary[jobRole].maxConcurrent = maxConcurrent;
		});

		// Calculate overall max concurrent
		const allTimePoints = new Set<number>();
		allPhases.forEach((p) => {
			allTimePoints.add(p.start);
			allTimePoints.add(p.end);
		});

		let maxConcurrentTotal = 0;
		Array.from(allTimePoints).forEach((time) => {
			const workersAtTime = allPhases
				.filter((p) => p.start <= time && p.end > time)
				.reduce((sum, p) => sum + p.workers, 0);
			maxConcurrentTotal = Math.max(maxConcurrentTotal, workersAtTime);
		});

		return { jobRoleSummary, maxConcurrentTotal };
	}, [standard]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: `${planner.radius}px`,
					boxShadow: planner.shadowDialog,
					fontFamily: planner.fontUi,
				},
			}}
		>
			<DialogTitle sx={{ bgcolor: planner.surface }}>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<Typography sx={{ ...planner.type.pageTitle, color: planner.navy }}>
						{standard.productName || standard.sku}
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Chip
							icon={<GroupIcon />}
							label={`${laborSummary.maxConcurrentTotal} workers (max concurrent)`}
							size="small"
							sx={chipAccentSx}
						/>
						<IconButton onClick={onClose} size="small" sx={{ color: planner.inkMuted }}>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ bgcolor: planner.canvas }}>
				{/* Standard Information */}
				<Paper elevation={0} sx={{ ...panelSx, mb: 3, mt: 1 }}>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Typography sx={{ ...planner.labelCaps, fontFamily: planner.fontUi, color: planner.accent }}>
								{allWorkstations.length > 1 ? "Work centers" : "Work center"}
							</Typography>
							<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi, color: planner.ink, mt: 0.25 }}>
								{allWorkstations.join(", ") || "—"}
							</Typography>
							{standard.recipeGroup ? (
								<Chip label={`Recipe ${standard.recipeGroup}`} size="small" sx={{ ...chipSx, mt: 0.75 }} />
							) : null}
						</Grid2>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Typography sx={{ ...planner.labelCaps, fontFamily: planner.fontUi, color: planner.accent }}>
								Batch Size
							</Typography>
							<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontMono, color: planner.ink, mt: 0.25 }}>
								{standard.batchSize.toLocaleString()} {standard.batchUnit || "units"}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Typography sx={{ ...planner.labelCaps, fontFamily: planner.fontUi, color: planner.accent }}>
								Total Duration
							</Typography>
							<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontMono, color: planner.ink, mt: 0.25 }}>
								{formatDuration(standard.totalDurationMinutes)}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Typography sx={{ ...planner.labelCaps, fontFamily: planner.fontUi, color: planner.accent }}>
								Operations
							</Typography>
							<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontMono, color: planner.ink, mt: 0.25 }}>
								{standard.operations?.length || 0} operations
							</Typography>
						</Grid2>
					</Grid2>
				</Paper>

				{/* Labor Summary */}
				<Box sx={{ mb: 3 }}>
					<Typography sx={{ ...planner.type.pageTitle, fontSize: 17, color: planner.navy, mb: 1 }}>
						Labor Requirements Summary
					</Typography>
					<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mb: 2 }}>
						Max concurrent workers needed: <strong>{laborSummary.maxConcurrentTotal}</strong> (sequential
						phases are handled by the same workers)
					</Typography>

					{/* Overall MO Timeline */}
					<OverallTimeline standard={standard} />

					{/* Job Role Chips — filled with the same ramp colour as the role's
					    timeline bars, so a pill can be matched to its bar by colour. */}
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						{Object.entries(laborSummary.jobRoleSummary).map(([role, data]) => {
							const fill = roleColor(role);
							const onFill = roleTextColor(fill);
							return (
								<Tooltip
									key={role}
									title={`${role}: ${data.maxConcurrent} workers at peak, ${Math.round((data.totalMinutes / 60) * 10) / 10} worker-hours total`}
								>
									<Chip
										icon={<GroupIcon sx={{ color: `${onFill} !important`, fontSize: 16 }} />}
										label={`${role}: ${data.maxConcurrent}`}
										size="small"
										sx={{
											borderRadius: `${planner.radiusSm}px`,
											bgcolor: fill,
											border: `1px solid ${fill}`,
											color: onFill,
											fontFamily: planner.fontUi,
											fontSize: 12,
											fontWeight: 600,
										}}
									/>
								</Tooltip>
							);
						})}
					</Box>
				</Box>

				<Divider sx={{ my: 2, borderColor: planner.hairline }} />

				{/* Operations Timeline */}
				<Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
						<Typography sx={{ ...planner.type.pageTitle, fontSize: 17, color: planner.navy }}>
							Operations Timeline
						</Typography>
						<Chip label={`${standard.operations?.length || 0} operations`} size="small" sx={chipSx} />
						<Chip
							label={`Total: ${formatDuration(standard.totalDurationMinutes)}`}
							size="small"
							sx={chipAccentSx}
						/>
					</Box>

					{standard.operations?.map((operation, opIdx) => (
						<OperationTimeline
							key={operation.id || opIdx}
							operationName={operationLabel(operation, `Operation ${opIdx + 1}`)}
							operationCategoryName={operationCategory(operation)}
							operationDuration={operation.durationMinutes}
							phases={operation.phases || []}
							sequence={opIdx + 1}
							workstation={operation.workstation || standard.workstation}
							alternatives={(operation.workstationOptions || []).map((option) => option.workstation)}
						/>
					))}
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default LaborStandardPreviewDialog;

